import { environment } from "@app/relay/relay-environment";
import { useCallback, useEffect, useState } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import type { useBudgetEditor_BudgetsQuery as BudgetsQuery } from "@app/queries/useBudgetEditor_BudgetsQuery.graphql";
import type { useBudgetEditor_BudgetQuery as BudgetQuery } from "@app/queries/useBudgetEditor_BudgetQuery.graphql";
import type { useBudgetEditor_UpsertMutation as UpsertMutation } from "@app/queries/useBudgetEditor_UpsertMutation.graphql";
import type { useBudgetEditor_DeleteMutation as DeleteMutation } from "@app/queries/useBudgetEditor_DeleteMutation.graphql";

// The "Actuals vs Planned" editor shared by the cost and revenue overview grids.
// Holds the planned-cell text the user types (keyed by the grid row's own
// identity + month), the list of saved budgets to pick from, and the load/save/
// delete plumbing. Budgets are a standalone annotation, so this talks to its own
// queries/mutations imperatively (the same fetchQuery/commitMutation pattern the
// overview pages already use) — no route-loader query ref to thread.

export type BudgetSide = "COST" | "REVENUE";

// Just what the toolbar selector renders. The /budgets page has its own richer
// query for the full listing.
export type BudgetSummary = {
  id: string;
  name: string;
};

const budgetsQuery = graphql`
  query useBudgetEditor_BudgetsQuery($side: BudgetSideEnum, $year: Int) {
    budgets(side: $side, year: $year) {
      id
      name
    }
  }
`;

const budgetQuery = graphql`
  query useBudgetEditor_BudgetQuery($id: ID!) {
    budget(id: $id) {
      id
      name
      year
      entries {
        rowKey
        period
        amountCents
      }
    }
  }
`;

const upsertMutation = graphql`
  mutation useBudgetEditor_UpsertMutation($input: BudgetUpsertInputObject!) {
    budgetUpsert(input: $input) {
      budget {
        id
        name
      }
      errors {
        message
      }
    }
  }
`;

const deleteMutation = graphql`
  mutation useBudgetEditor_DeleteMutation($input: BudgetDeleteInputObject!) {
    budgetDelete(input: $input) {
      budget {
        id
      }
      errors {
        message
      }
    }
  }
`;

const cellKey = (rowKey: string, period: string) => `${rowKey}|${period}`;

// A typed dollar string → integer cents, or null when blank/unparseable (an
// absent cell means "no budget", never zero).
export function dollarsToCents(text: string): number | null {
  const cleaned = text.replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100);
}

const centsToText = (cents: number) => (cents / 100).toString();

export type BudgetEditor = {
  side: BudgetSide;
  mode: boolean;
  setMode: (on: boolean) => void;
  budgets: BudgetSummary[];
  selectedId: string | null;
  name: string;
  setName: (name: string) => void;
  dirty: boolean;
  busy: boolean;
  hasCells: boolean;
  selectBudget: (id: string | null) => Promise<{ year: number } | null>;
  budgetText: (rowKey: string, period: string) => string;
  setBudgetText: (rowKey: string, period: string, text: string) => void;
  budgetDollars: (rowKey: string, period: string) => number | null;
  save: () => Promise<void>;
  remove: () => Promise<void>;
};

export function useBudgetEditor(config: {
  side: BudgetSide;
  year: number;
  perspectiveId?: string | null;
  basis?: string | null;
}): BudgetEditor {
  const { side, year, perspectiveId = null, basis = null } = config;
  const [mode, setModeState] = useState(false);
  const [budgets, setBudgets] = useState<BudgetSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [text, setText] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const reloadList = useCallback(() => {
    fetchQuery<BudgetsQuery>(environment, budgetsQuery, { side, year })
      .toPromise()
      .then((data) => {
        if (data) setBudgets(data.budgets.map((b) => ({ id: b.id, name: b.name })));
      });
  }, [side, year]);

  // Refresh the selector whenever side/year change.
  useEffect(() => {
    reloadList();
  }, [reloadList]);

  const reset = useCallback(() => {
    setSelectedId(null);
    setName("");
    setText({});
    setDirty(false);
  }, []);

  const setMode = useCallback(
    (on: boolean) => {
      setModeState(on);
      if (!on) reset();
    },
    [reset]
  );

  const selectBudget = useCallback(
    async (id: string | null) => {
      if (id === null) {
        reset();
        return null;
      }
      const data = await fetchQuery<BudgetQuery>(environment, budgetQuery, { id }).toPromise();
      const loaded = data?.budget;
      if (!loaded) return null;
      const next: Record<string, string> = {};
      for (const entry of loaded.entries) next[cellKey(entry.rowKey, entry.period)] = centsToText(entry.amountCents);
      setSelectedId(loaded.id);
      setName(loaded.name);
      setText(next);
      setDirty(false);
      return { year: loaded.year };
    },
    [reset]
  );

  const budgetText = useCallback((rowKey: string, period: string) => text[cellKey(rowKey, period)] ?? "", [text]);

  const setBudgetText = useCallback((rowKey: string, period: string, value: string) => {
    setText((prev) => ({ ...prev, [cellKey(rowKey, period)]: value }));
    setDirty(true);
  }, []);

  const budgetDollars = useCallback(
    (rowKey: string, period: string) => {
      const cents = dollarsToCents(text[cellKey(rowKey, period)] ?? "");
      return cents === null ? null : cents / 100;
    },
    [text]
  );

  const save = useCallback(async () => {
    if (!name.trim()) {
      window.alert("Name your budget before saving.");
      return;
    }
    const entries = Object.entries(text).flatMap(([key, value]) => {
      const cents = dollarsToCents(value);
      if (cents === null) return [];
      // period is the fixed-width "YYYY-MM" suffix; split on the last pipe so a
      // rowKey is free to contain pipes.
      const pipe = key.lastIndexOf("|");
      return [{ rowKey: key.slice(0, pipe), period: key.slice(pipe + 1), amountCents: cents }];
    });
    setBusy(true);
    await new Promise<void>((resolve) => {
      commitMutation<UpsertMutation>(environment, {
        mutation: upsertMutation,
        variables: {
          input: {
            id: selectedId,
            name: name.trim(),
            side,
            year,
            perspectiveId: side === "COST" ? perspectiveId : null,
            basis: side === "COST" ? basis : null,
            entries,
          },
        },
        onCompleted: (response, errors) => {
          setBusy(false);
          if (errors?.length) {
            window.alert(errors[0].message);
            resolve();
            return;
          }
          const saved = response.budgetUpsert?.budget;
          if (saved) {
            setSelectedId(saved.id);
            setName(saved.name);
          }
          setDirty(false);
          reloadList();
          resolve();
        },
        onError: (error) => {
          setBusy(false);
          window.alert(error.message);
          resolve();
        },
      });
    });
  }, [name, text, selectedId, side, year, perspectiveId, basis, reloadList]);

  const remove = useCallback(async () => {
    if (!selectedId) return;
    if (!window.confirm("Delete this budget? This cannot be undone.")) return;
    setBusy(true);
    await new Promise<void>((resolve) => {
      commitMutation<DeleteMutation>(environment, {
        mutation: deleteMutation,
        variables: { input: { id: selectedId } },
        onCompleted: (_response, errors) => {
          setBusy(false);
          if (errors?.length) window.alert(errors[0].message);
          reset();
          reloadList();
          resolve();
        },
        onError: (error) => {
          setBusy(false);
          window.alert(error.message);
          resolve();
        },
      });
    });
  }, [selectedId, reloadList]);

  const hasCells = Object.values(text).some((value) => dollarsToCents(value) !== null);

  return {
    side,
    mode,
    setMode,
    budgets,
    selectedId,
    name,
    setName,
    dirty,
    busy,
    hasCells,
    selectBudget,
    budgetText,
    setBudgetText,
    budgetDollars,
    save,
    remove,
  };
}
